<script setup lang="ts">
  import { Motion } from 'motion-v'
  import { type HTMLAttributes, computed } from 'vue'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits
  } from 'radix-vue'
  import { X } from 'lucide-vue-next'
  import {
    buildFadeTransition,
    buildModalStaggerTransition,
    modalContentPreset,
    motionTiming
  } from '@/utils/motion'
  import { cn } from '@/utils'

  const props = defineProps<
    DialogContentProps & { class?: HTMLAttributes['class']; hideClose?: boolean }
  >()
  const emits = defineEmits<DialogContentEmits>()

  const delegatedProps = computed(() => {
    const { class: _, hideClose: __, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const modalContentTransition = {
    ...buildFadeTransition(motionTiming.durationMd),
    ...buildModalStaggerTransition()
  }
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 bg-[rgba(0,0,0,0.6)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200"
    />

    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 bg-background gap-4 border p-6 shadow-lg rounded-2xl overflow-hidden',
          props.class
        )
      "
    >
      <Motion
        :initial="modalContentPreset.initial"
        :animate="modalContentPreset.animate"
        :transition="modalContentTransition"
        class="relative flex h-full min-h-0 flex-col"
      >
        <slot />

        <DialogClose
          v-if="!props.hideClose"
          class="absolute end-0 top-0 p-3 rounded-sm ring-offset-background transition-opacity focus:outline-none disabled:pointer-events-none focus:ring-2 focus:ring-ring focus:ring-offset-2 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <div
            class="flex p-2 items-center justify-center rounded-full bg-overlay-black-5 hover:bg-overlay-black-10 transition-colors"
          >
            <X class="w-5 h-5 text-shade-primary" />
          </div>
        </DialogClose>
      </Motion>
    </DialogContent>
  </DialogPortal>
</template>
